import { getCSRFToken } from '@aytm/lib/common/rails-post-fetch'

// What the server sends when it renders an error page instead of the file it was asked for.
const NOT_A_FILE = /^(?:text\/html|application\/json)/i

const DISPOSITION_FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i

/** The name the server asked for, falling back to the caller's when it names none. */
export const filenameFromResponse = (response, fallback) => {
  const matched = DISPOSITION_FILENAME.exec(response.headers.get('content-disposition') || '')
  return matched ? decodeURIComponent(matched[1]) : fallback
}

/** Hand a blob to the browser as a download. */
export const saveBlob = (blob, filename) => {
  const urlObject = window.URL || window.webkitURL
  const href = urlObject.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = href
  anchor.download = filename || ''
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  urlObject.revokeObjectURL(href)
}

/**
 * POST `data` and save what comes back as a file.
 *
 * Replaces the `$.fileDownload` jQuery plugin. The plugin could not observe its own request, so
 * it inferred failure from a cookie the server had to set, and callers signalled completion the
 * moment the request was fired. A fetch resolves after the body has arrived, so completion and
 * failure are both real here: await it, and one `catch` covers both a transport error and a
 * server that answered with an error page.
 */
export const downloadFile = async ({ url, data = {}, filename, signal } = {}) => {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(data)) {
    if (undefined !== value && null !== value) body.append(key, value)
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-cache',
    headers: { 'X-CSRF-Token': getCSRFToken() },
    body,
    signal
  })

  if (!response.ok) throw new Error(`file download refused: ${response.status} ${response.statusText}`)

  // A Rails error page can arrive with a 200, so the status alone does not say a file was sent.
  const contentType = response.headers.get('content-type') || ''
  if (NOT_A_FILE.test(contentType)) throw new Error(`file download returned ${contentType} rather than a file`)

  const blob = await response.blob()
  if (0 === blob.size) throw new Error('file download returned an empty body')

  saveBlob(blob, filenameFromResponse(response, filename))

  return { filename: filenameFromResponse(response, filename), size: blob.size }
}
